export type MetricDimensionUnit = "none" | "mm" | "cm" | "m";
export type ImperialDimensionUnit = "none" | "in" | "ft";
export type DimensionOutputUnit = Exclude<MetricDimensionUnit | ImperialDimensionUnit, "none">;
export type DimensionUnits =
  | "model"
  | "mm"
  | "cm"
  | "m"
  | "in"
  | "ft"
  | "both"
  | "mm+in"
  | "mm+ft"
  | "cm+in"
  | "cm+ft"
  | "m+in"
  | "m+ft";

export const metricDimensionUnits: readonly [MetricDimensionUnit, string][] = [
  ["none", "None"],
  ["mm", "mm"],
  ["cm", "cm"],
  ["m", "m"],
];

export const imperialDimensionUnits: readonly [ImperialDimensionUnit, string][] = [
  ["none", "None"],
  ["in", "in"],
  ["ft", "ft"],
];

const METRIC_TO_MM: Record<Exclude<MetricDimensionUnit, "none">, number> = {
  mm: 1,
  cm: 10,
  m: 1000,
};

const IMPERIAL_TO_MM: Record<Exclude<ImperialDimensionUnit, "none">, number> = {
  in: 25.4,
  ft: 304.8,
};

const OUTPUT_PRECISION: Record<DimensionOutputUnit, number> = {
  mm: 1,
  cm: 2,
  m: 3,
  in: 2,
  ft: 3,
};

const DIMENSION_UNIT_VALUES = new Set([
  "model",
  "mm",
  "cm",
  "m",
  "in",
  "ft",
  "both",
  "mm+in",
  "mm+ft",
  "cm+in",
  "cm+ft",
  "m+in",
  "m+ft",
]);

export function isDimensionUnitsValue(value: unknown): value is DimensionUnits {
  return typeof value === "string" && DIMENSION_UNIT_VALUES.has(value);
}

export function dimensionUnitSelection(
  value: DimensionUnits,
  modelUnits?: string,
): {
  metric: MetricDimensionUnit;
  imperial: ImperialDimensionUnit;
} {
  if (value === "model") return modelUnitSelection(modelUnits);
  if (value === "both") return { metric: "mm", imperial: "in" };
  if (value.includes("+")) {
    const [metric, imperial] = value.split("+");
    return {
      metric: metric as MetricDimensionUnit,
      imperial: imperial as ImperialDimensionUnit,
    };
  }
  if (value === "mm" || value === "cm" || value === "m") {
    return { metric: value, imperial: "none" };
  }
  if (value === "in" || value === "ft") return { metric: "none", imperial: value };
  return { metric: "none", imperial: "none" };
}

function modelUnitSelection(modelUnits?: string): {
  metric: MetricDimensionUnit;
  imperial: ImperialDimensionUnit;
} {
  switch (modelUnits) {
    case "millimetres":
    case "millimeters":
    case "millimetre":
    case "millimeter":
      return { metric: "mm", imperial: "none" };
    case "centimetres":
    case "centimeters":
    case "centimetre":
    case "centimeter":
      return { metric: "cm", imperial: "none" };
    case "metres":
    case "meters":
    case "metre":
    case "meter":
      return { metric: "m", imperial: "none" };
    case "inches":
    case "inch":
      return { metric: "none", imperial: "in" };
    case "feet":
    case "foot":
      return { metric: "none", imperial: "ft" };
    default:
      // A file without a recognised unit is read in millimetres, as in Rust.
      return { metric: "mm", imperial: "none" };
  }
}

export function dimensionUnitsFor(
  metric: MetricDimensionUnit,
  imperial: ImperialDimensionUnit,
): DimensionUnits {
  if (metric === "none" && imperial === "none") return "model";
  if (metric === "none") return imperial === "none" ? "model" : imperial;
  if (imperial === "none") return metric;
  return `${metric}+${imperial}` as DimensionUnits;
}

export function modelUnitsToMillimetres(modelUnits: string) {
  if (
    modelUnits === "metres" ||
    modelUnits === "meters" ||
    modelUnits === "metre" ||
    modelUnits === "meter"
  )
    return 1000;
  if (
    modelUnits === "centimetres" ||
    modelUnits === "centimeters" ||
    modelUnits === "centimetre" ||
    modelUnits === "centimeter"
  )
    return 10;
  if (modelUnits === "inches" || modelUnits === "inch") return 25.4;
  if (modelUnits === "feet" || modelUnits === "foot") return 304.8;
  return 1;
}

export function modelUnitLabel(modelUnits: string) {
  if (
    modelUnits === "metres" ||
    modelUnits === "meters" ||
    modelUnits === "metre" ||
    modelUnits === "meter"
  )
    return "m";
  if (
    modelUnits === "centimetres" ||
    modelUnits === "centimeters" ||
    modelUnits === "centimetre" ||
    modelUnits === "centimeter"
  )
    return "cm";
  if (
    modelUnits === "millimetres" ||
    modelUnits === "millimeters" ||
    modelUnits === "millimetre" ||
    modelUnits === "millimeter"
  )
    return "mm";
  if (modelUnits === "inches" || modelUnits === "inch") return "in";
  if (modelUnits === "feet" || modelUnits === "foot") return "ft";
  return "mm";
}

/**
 * `value` to `decimals` places, half away from zero. A measured value carries
 * noise far below what is shown (sheet coordinates travel to a thousandth of
 * a point), so it is rounded to two more places first: a CAD size that sits
 * on the half, such as a 0.025 in radius, then reads the same every time
 * instead of flipping with each layout.
 */
export function formatFixed(value: number, decimals: number) {
  const settled = Number(value.toFixed(decimals + 2));
  const scaled = Number((Math.abs(settled) * 10 ** decimals).toPrecision(12));
  return (Math.sign(settled) * (Math.round(scaled) / 10 ** decimals)).toFixed(decimals);
}

export function formatDimensionValue(millimetres: number, unit: DimensionOutputUnit) {
  const conversion =
    unit in METRIC_TO_MM
      ? METRIC_TO_MM[unit as keyof typeof METRIC_TO_MM]
      : IMPERIAL_TO_MM[unit as keyof typeof IMPERIAL_TO_MM];
  return `${formatFixed(millimetres / conversion, OUTPUT_PRECISION[unit])} ${unit}`;
}

export function formatDimension(value: number, modelUnits: string, units: DimensionUnits) {
  if (units === "model") {
    const label = modelUnitLabel(modelUnits);
    const precision =
      label === "cm" ? 2 : label === "m" || label === "ft" ? 3 : label === "in" ? 2 : 1;
    return `${formatFixed(value / modelUnitsToMillimetres(modelUnits), precision)} ${label}`;
  }
  const { metric, imperial } = dimensionUnitSelection(units);
  const labels: string[] = [];
  if (metric !== "none") labels.push(formatDimensionValue(value, metric));
  if (imperial !== "none") labels.push(formatDimensionValue(value, imperial));
  return labels.join("\n");
}

export function unitToMillimetres(unit: DimensionOutputUnit) {
  return unit in METRIC_TO_MM
    ? METRIC_TO_MM[unit as keyof typeof METRIC_TO_MM]
    : IMPERIAL_TO_MM[unit as keyof typeof IMPERIAL_TO_MM];
}

/**
 * The first unit a dimension is shown in, with the value in that unit and its
 * decimal places; tolerances and limits are written in it.
 */
export function primaryDimensionValue(
  millimetres: number,
  modelUnits: string,
  units: DimensionUnits,
) {
  const { metric, imperial } = dimensionUnitSelection(units, modelUnits);
  const unit: DimensionOutputUnit =
    metric !== "none" ? metric : imperial !== "none" ? imperial : "mm";
  return { value: millimetres / unitToMillimetres(unit), digits: OUTPUT_PRECISION[unit], unit };
}

/**
 * Model-unit lengths (`power` 1), areas (2) or volumes (3) in display units:
 * the model's own unit for "model" (the values as they are), otherwise each
 * chosen metric and imperial unit, joined by " / ". Several values (a box's
 * sides) share one unit: "150 × 88.5 × 27 mm".
 */
export function formatQuantity(
  values: number | readonly number[],
  modelUnits: string,
  units: DimensionUnits,
  power: 1 | 2 | 3 = 1,
  separator = " × ",
) {
  const list = typeof values === "number" ? [values] : values;
  const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 });
  const suffix = power === 2 ? "²" : power === 3 ? "³" : "";
  const shown = (label: string, factor: number) =>
    `${list.map((value) => (Number.isFinite(value) ? number.format(value * factor) : "—")).join(separator)} ${label}${suffix}`;
  if (units !== "model") {
    const { metric, imperial } = dimensionUnitSelection(units, modelUnits);
    const chosen = [metric, imperial].filter(
      (unit): unit is DimensionOutputUnit => unit !== "none",
    );
    const perModel = modelUnitsToMillimetres(modelUnits);
    if (chosen.length)
      return chosen
        .map((unit) => shown(unit, (perModel / unitToMillimetres(unit)) ** power))
        .join(" / ");
  }
  return shown(modelUnitLabel(modelUnits), 1);
}

const DEFAULT_TOLERANCE: Record<DimensionOutputUnit, string> = {
  mm: "±0.1 MM",
  cm: "±0.01 CM",
  m: "±0.0001 M",
  in: "±0.005 IN",
  ft: "±0.0004 FT",
};

/**
 * The general tolerance a new drawing states in its title block, in the
 * units its dimensions are shown in. The same text as Rust's
 * `default_tolerance_note`, which writes it when the drawing is made.
 */
export function defaultToleranceNote(units: DimensionUnits, modelUnits?: string) {
  const { metric, imperial } = dimensionUnitSelection(units, modelUnits);
  const stated = [metric, imperial]
    .filter((unit): unit is DimensionOutputUnit => unit !== "none")
    .map((unit) => DEFAULT_TOLERANCE[unit]);
  return `UNLESS OTHERWISE SPECIFIED: ${(stated.length ? stated : [DEFAULT_TOLERANCE.mm]).join(" / ")}`;
}

/**
 * The title block after the dimension units change: a tolerance note still
 * saying the default for the old units follows the new ones, and one the
 * user wrote is kept as it is.
 */
export function titleForDimensionUnits<T extends { tolerances: string }>(
  title: T,
  from: DimensionUnits,
  to: DimensionUnits,
  modelUnits?: string,
): T {
  if (title.tolerances !== defaultToleranceNote(from, modelUnits)) return title;
  const tolerances = defaultToleranceNote(to, modelUnits);
  return tolerances === title.tolerances ? title : { ...title, tolerances };
}
